<template>
  <StepItem
    format="form"
    :title="store.isGiveaway ? 'Enter Our Taylor Swift Giveaway' : 'Find your dream career'"
    :subtitle="store.isGiveaway ? 'By joining Business Draft in 2 easy steps or use the' : 'Join Business Draft in 2 easy steps'"
    @handle-next-step="handleNextStep"
    :loading="loading"
    :is-ready-for-next-step="isReadyForNextStep || hasLogInDetailsForGiveaway">
    <a v-if="store.isGiveaway" target="_blank"
       href="https://resources.businessdraft.com/business-draft-taylor-swift-contest-mail-entrant-form.pdf"
       class="mx-auto -mt-8 mb-8 block max-w-fit text-center opacity-50 transition-all hover:opacity-100">
      <FormButton use-div color="outline" size="sm" class="mx-auto max-w-fit">Mail Entrant Form</FormButton>
    </a>
    <button type="button" @click="toggleLogIn" v-if="store.isGiveaway"
            class="mb-6 cursor-pointer text-sm text-slate-400 underline hover:text-slate-900">
      {{ showLogIn ? "Sign Up For New Account Instead" : "Sign Into Existing Account Instead" }}
    </button>
    <template v-if="showLogIn">
      <FormInput
        class="mb-4 w-full"
        type="email"
        input-mode="email"
        required
        label="Email Address"
        placeholder="Email address"
        v-model="store.emailAddress"/>
      <FormInput
        class="mb-4 w-full"
        type="password"
        required
        label="Password"
        placeholder="••••••••"
        v-model="store.password"/>
    </template>
    <template v-else>
      <div class="mb-4 flex items-center justify-between gap-4 md:gap-6">
        <FormInput
          class="w-full"
          required
          label="First Name"
          placeholder="First name"
          v-model="store.firstName"
          :error="store?.errors?.firstName"
          @update:modelValue="handleUpdateName"
          autocomplete="given-name"
        />
        <FormInput
          class="w-full"
          required
          label="Last Name"
          placeholder="Last name"
          v-model="store.lastName"
          :error="store?.errors?.lastName"
          @update:modelValue="handleUpdateName"
          autocomplete="family-name"
        />
      </div>
      <FormInput
        class="w-full"
        type="email"
        input-mode="email"
        required
        label="Email Address"
        placeholder="youre@awesome.com"
        v-model="store.emailAddress"
        :error="store?.errors?.emailAddress"
        @update:modelValue="throttleValidateCurrentStep();"
      />
      <FormEmailCompleter
        v-model="store.emailAddress"
        class="mt-2 mb-4"
      />
      <p v-if="emailExists" class="-mt-2 mb-6 text-sm text-green-600">
        This email already has an account!
        <RouterLink :to="`/login?email=${lastCheckedEmail}`">
          <span class="underline">Log In Now</span>
        </RouterLink>
      </p>
      <FormInput
        class="mb-6 w-full"
        format="username"
        required
        label="Username"
        placeholder="@username"
        v-model="store.username"
        :error="store?.errors?.username"
        @update:modelValue="throttleValidateCurrentStep"
      />
      <FormCheckbox
        v-model="store.enableDefaultSmsNotifications" class="mb-4"
        :error="store?.errors?.enableDefaultSmsNotifications">
        Send time-sensitive notifications to me via text messages (recommended)
      </FormCheckbox>
    </template>
    <FormCheckbox
      v-model="store.tosChecked"
      :error="store?.errors?.tosChecked"
      @update:modelValue="validateCurrentStep(false)"
    >
      I agree to the
      <a href="/privacy-policy" target="_blank" tabindex="-1"><u>Privacy Policy</u></a>
      <span v-if="store.isGiveaway">, the </span>
      <span v-else> and the </span>
      <a href="/terms-of-service" target="_blank" tabindex="-1"><u>Terms of Service</u></a>
      <span v-if="store.isGiveaway">, and the
        <a href="/giveaway-rules"><u>Official Giveaway Rules</u></a>,</span>
      including that I am over 18 years old and I can legally work in the
      United States. This site is protected by reCAPTCHA and the Google
      <a
        href="https://policies.google.com/privacy"
        rel="nofollow"
        target="_blank"
        tabindex="-1"
      ><u>Privacy Policy</u></a>
      and
      <a
        href="https://policies.google.com/terms"
        rel="nofollow"
        target="_blank"
        tabindex="-1"
      ><u>Terms of Service</u></a>
      apply.
    </FormCheckbox>
    <FormButton v-if="store.isGiveaway && !showReferralCode && !store.useReferralCode && !showLogIn" @click="showReferralCode=true"
                color="outline" size="xs" class="mx-auto mt-4 opacity-60">
      Add Referral Code
    </FormButton>
    <FormInput
      v-else-if="store.isGiveaway && !showLogIn && showReferralCode"
      class="my-6 w-full"
      label="Referral Code"
      placeholder="Referral code (optional)"
      v-model="store.useReferralCode"
      :error="store?.errors?.useReferralCode"
      @update:modelValue="updateReferralCode">
      <template #near-input>
        <div class="absolute inset-y-0 right-0 flex items-center pr-3 text-sm space-x-1"
             :class="isValidReferralCode ? 'text-green-500' : 'text-red-500'">
          <AnimationSpinningLoader v-if="loadingIsValidReferralCode" color="#000"/>
          <CheckIcon v-else-if="isValidReferralCode" class="h-4 w-4"/>
          <XMarkIcon v-else class="h-4 w-4"/>
          <span v-if="!loadingIsValidReferralCode">{{ isValidReferralCode ? "Valid" : "Invalid" }} Code</span>
        </div>
      </template>
    </FormInput>
  </StepItem>
</template>

<script setup>
import AnimationSpinningLoader from "./AnimationSpinningLoader.vue";
import StepItem from "./StepItem.vue";
import FormButton from "./FormButton.vue";
import FormInput from "./FormInput.vue";
import FormCheckbox from "./FormCheckbox.vue";
import {computed, onMounted, ref, inject, onActivated} from "vue";
import {CheckIcon, XMarkIcon} from "@heroicons/vue/24/solid";
import {useCandidateAccountCreateStore} from "../stores/CandidateAccountCreateStore";
import {debounce, throttle} from "lodash";
import {checkIfUsernameTaken} from "../helpers/CommonAccountFunctions.js";
import {useAccountStore} from "../stores/AccountStore.js";
import {getItem} from "../helpers/LocalStorage.js";
import FormEmailCompleter from "./FormEmailCompleter.vue";

const emits = defineEmits(["complete"]);

const accountStore = useAccountStore();
const store = useCandidateAccountCreateStore();
const isReadyForNextStep = ref(false);
const lastCheckedEmail = ref("");
const emailExists = ref(false);
const usernameTaken = ref(false);
const loading = ref(false);
const showReferralCode = ref(false);
const loadingIsValidReferralCode = ref(false);
const isValidReferralCode = ref(false);
const showLogIn = ref(false);
const enterExistingUserIntoGiveaway = inject("enterExistingUserIntoGiveaway", null);

const handleUpdateName = debounce(() => {
  const firstName = store.firstName.replace(/[^a-zA-Z]/g, "");
  const lastName = store.lastName.replace(/[^a-zA-Z]/g, "");

  const separator = firstName && lastName ? "." : "";

  store.username = `${firstName}${separator}${lastName}`.toLowerCase();
  throttleValidateCurrentStep();
});

const toggleLogIn = () => showLogIn.value = !showLogIn.value;

const handleEnterGiveawayWithExistingUser = async () => {
  try {
    const response = await accountStore.login(store.emailAddress, store.password, true, getItem("setCompanyId"));
    if (!response?.data?.id && response?.data) return loading.value = false;
    if (enterExistingUserIntoGiveaway) await enterExistingUserIntoGiveaway();
  } catch (e) {
    console.error(e);
  } finally {
    loading.value = false;
  }
};

const handleNextStep = async () => {
  if (loading.value) return;
  loading.value = true;

  if (showLogIn.value) return await handleEnterGiveawayWithExistingUser();

  try {
    validateCurrentStep(true);
    if (isReadyForNextStep.value) {
      lastCheckedEmail.value = encodeURIComponent(store.emailAddress);
      emailExists.value = !!await handleCheckEmailExists();
      if (emailExists.value) {
        if (store.isGiveaway) showLogIn.value = true;
        loading.value = false;
        return;
      }
      usernameTaken.value = !!await handleCheckUsernameTaken();
      if (usernameTaken.value) return loading.value = false;
      emits("complete");
    }
  } catch (e) {
    console.error(e);
  }

  loading.value = false;
};

const validateCurrentStep = (showToast = false) =>
  (isReadyForNextStep.value =
    store.validateStep1(showToast));

const handleCheckEmailExists = async () => {
  const emailExists = await store.checkIfEmailExists();
  if (emailExists) toast.error("That email address is already in use. Please sign in instead.");
  return emailExists;
};

const handleCheckValidReferralCode = async () => {
  try {
    const hasReferralCode = !!store.useReferralCode;
    const {taken} = await checkIfUsernameTaken(store.useReferralCode);
    isValidReferralCode.value = hasReferralCode && taken;
  } catch (e) {
    isValidReferralCode.value = false;
  } finally {
    loadingIsValidReferralCode.value = false;
  }
};

const debounceHandleCheckValidReferralCode = debounce(handleCheckValidReferralCode, 500);

const updateReferralCode = () => {
  loadingIsValidReferralCode.value = true;
  debounceHandleCheckValidReferralCode();
};

const handleCheckUsernameTaken = async () => {
  const status = await store.checkIfUsernameTaken();
  if (status.taken) toast.error("That username is already taken. Please try another one.");
  return status.taken || !status.valid || !!status.reason;
};

const handlePrefillReferralCode = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const referralCode = searchParams.get("ref");
  if (referralCode) {
    loadingIsValidReferralCode.value = true;
    store.useReferralCode = decodeURI(referralCode);
    handleCheckValidReferralCode();
  }
};

const throttleValidateCurrentStep = throttle(() => validateCurrentStep(false), 300);

const handleQueryParams = () => {
  const params = new URLSearchParams(window.location.search);
  const email = params.get("email");
  if (email) store.emailAddress = email;
};

const hasLogInDetailsForGiveaway = computed(() => !!store.emailAddress && !!store.password && !!store.tosChecked && showLogIn.value);

onActivated(() => {
  if (accountStore.isLoggedIn) return emits("complete");
});

onMounted(() => {
  if (accountStore.isLoggedIn) return emits("complete");
  if (store.isGiveaway) handlePrefillReferralCode();
  handleQueryParams();
});
</script>
